import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { ArtistProfileDialog } from './ProArtistCard';
import { getScaleDifficulty, SCALE_DIFFICULTY_LABELS, getDisplayRootNote } from '../utils/musicTheory';
import { ACCENT_COLOR } from '../config/uiTheme';
import { bindBackingMedia } from '../utils/playbackMix';
import { setNowPlaying, setNowPlayingState, setNowPlayingHandlers } from '../utils/mediaSession';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import LockIcon from '@mui/icons-material/Lock';

// Difficulty badge colors: green = safe, amber = some tension, red = use your ears.
const DIFFICULTY_COLORS = { easy: '#10b981', intermediate: '#f59e0b', advanced: '#ef4444' };
const DIFFICULTY_ORDER = { easy: 0, intermediate: 1, advanced: 2 };

/**
 * BackingTrackCard - Audio player component for backing tracks
 * Matches VideoCard styling with audio player controls
 */
const BackingTrackCard = ({
  track,
  artist,
  badge = 'Free',
  badgeColor = '#10b981',
  currentMode,
  currentRoot,
  onJam,
  onPreviewStart,
  // Gating par pack : un backing verrouille garde le Preview mais Jam ouvre
  // le dialog d'achat du pack (onUnlock).
  unlocked = true,
  onUnlock
}) => {
  const audioRef = useRef(null);
  useEffect(() => bindBackingMedia(audioRef.current), []);
  const [isPlaying, setIsPlaying] = useState(false);
  // Boucle toujours active (le bouton toggle a ete retire de la card).
  const [isLooping] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showProfile, setShowProfile] = useState(false);

  // Sur un multi-tonal, un rung est un TYPE de gamme applique a la root de chaque
  // section (le player suit les key centers) ; sur un mono-tonal c'est une gamme
  // ancree a la root de la piste.
  const isMultiTonal = (track.keyCenters || []).length > 1;

  // A ladder rung matches the user's current context only when both the scale AND
  // the root line up (an E backing's "Aeolian" rung is E Aeolian, not the user's C Aeolian).
  const matchesCurrent = (scale) => scale === currentMode && (isMultiTonal
    ? (track.keyCenters || []).some(k => k.root === currentRoot)
    : track.rootNote === currentRoot);

  // Niveau du morceau lui-meme : editorial (fiche) sinon repli automatique, un
  // multi-tonal ne peut pas etre "easy" (les roots changent en cours de lecture).
  const trackDifficulty = track.difficulty || (isMultiTonal ? 'intermediate' : null);
  const effectiveDifficulty = (scaleDiff) => {
    if (!trackDifficulty) return scaleDiff;
    return DIFFICULTY_ORDER[trackDifficulty] > DIFFICULTY_ORDER[scaleDiff] ? trackDifficulty : scaleDiff;
  };

  // Difficulty ladder: one rung per compatible scale, rated by how hard it is to
  // improvise over this backing. The current scale is pinned first, then easiest-first.
  const ladder = (track.compatibleScales || [])
    .map((scale) => ({ scale, difficulty: effectiveDifficulty(getScaleDifficulty(scale)) }))
    .sort((a, b) => {
      if (matchesCurrent(a.scale)) return -1;
      if (matchesCurrent(b.scale)) return 1;
      return DIFFICULTY_ORDER[a.difficulty] - DIFFICULTY_ORDER[b.difficulty];
    });
  const bestScale = ladder.find((r) => matchesCurrent(r.scale))?.scale
    || track.mode
    || ladder[0]?.scale;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      if (!isLooping) {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isLooping]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isLooping;
    }
  }, [isLooping]);

  useEffect(() => {
    const handlePlayEvent = (e) => {
      if (e.detail?.trackId === track.id) return;
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    };
    const handleStopAll = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener('backingtrack:play', handlePlayEvent);
    document.addEventListener('backingtrack:stopall', handleStopAll);
    return () => {
      document.removeEventListener('backingtrack:play', handlePlayEvent);
      document.removeEventListener('backingtrack:stopall', handleStopAll);
    };
  }, [track.id]);

  // Preview lancee depuis la liste : meme fiche systeme que le player de jam,
  // sinon l'ecran verrouille affiche le hostname de la page. Pose seulement
  // pendant la lecture, une carte qui ne joue pas n'a rien a piloter.
  useEffect(() => {
    if (!isPlaying) return undefined;
    setNowPlaying({ title: track.title, artist: artist?.name, artwork: artist?.avatar });
    setNowPlayingState(true);
    setNowPlayingHandlers({
      play: () => {
        const audio = audioRef.current;
        if (audio && audio.paused) { audio.play().catch(() => {}); setIsPlaying(true); }
      },
      pause: () => {
        const audio = audioRef.current;
        if (audio && !audio.paused) { audio.pause(); setIsPlaying(false); }
      },
      stop: () => {
        const audio = audioRef.current;
        if (audio) { audio.pause(); audio.currentTime = 0; }
        setIsPlaying(false);
      },
    });
    return () => setNowPlayingState(false);
  }, [isPlaying, track.title, artist?.name, artist?.avatar]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      if (onPreviewStart) {
        onPreviewStart();
      }
      document.dispatchEvent(new CustomEvent('backingtrack:play', { detail: { trackId: track.id } }));
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleJam = (scale) => {
    if (!unlocked) {
      if (onUnlock) onUnlock();
      return;
    }
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    if (onJam) onJam(scale);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accentColor = ACCENT_COLOR;
  // Vignette : artwork de la piste, sinon photo de l'artiste (les backings n'ont
  // pas tous un thumbnail dedie).
  const coverUrl = track.thumbnailUrl || artist?.avatar;

  return (
    <Box className="jam-backing-card" sx={{
      backgroundColor: 'rgba(30, 30, 40, 0.6)',
      borderRadius: '16px',
      overflow: 'hidden',
      mb: 2,
      '@container jam-tracks (min-width: 600px)': {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        '& > .jam-backing-cover': { paddingTop: 0, minHeight: 280 },
        '& > .jam-backing-info': { p: 2, minWidth: 0 }
      }
    }}>
      {/* Hidden audio element */}
      <audio ref={audioRef} crossOrigin="anonymous" src={track.audioUrl} preload="metadata" />

      {/* Thumbnail area - matches VideoCard 16:9 ratio */}
      <Box className="jam-backing-cover" sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: 'rgba(60, 60, 80, 0.4)',
        ...(coverUrl && {
          backgroundImage: `url(${coverUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        })
      }}>
        {/* Degrade sombre : garde Preview/Jam lisibles par-dessus l'artwork */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: coverUrl
            ? 'linear-gradient(to top, rgba(10,10,16,0.75) 0%, rgba(10,10,16,0.35) 60%, rgba(10,10,16,0.15) 100%)'
            : 'rgba(10, 10, 16, 0.2)'
        }} />

        {/* Preview/Jam pills - centered */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          gap: 1.5
        }}>
          {/* Preview pill */}
          <Box
            onClick={togglePlay}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2.5,
              py: 1.25,
              borderRadius: '999px',
              cursor: 'pointer',
              backgroundColor: isPlaying ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isPlaying ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.6)'
              }
            }}
          >
            {isPlaying ? (
              <PauseIcon sx={{ fontSize: 22, color: 'white' }} />
            ) : (
              <PlayArrowIcon sx={{ fontSize: 22, color: 'white' }} />
            )}
            <Typography sx={{ fontSize: '0.9rem', color: 'white', fontWeight: 600 }}>
              Preview
            </Typography>
          </Box>

          {/* Jam pill (Unlock quand le pack n'est pas achete) */}
          <Box
            onClick={() => handleJam(bestScale)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: unlocked ? 0 : 0.75,
              px: unlocked ? 3.5 : 2.5,
              py: 1.25,
              borderRadius: '999px',
              cursor: 'pointer',
              backgroundColor: 'var(--button)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'scale(1.04)',
                backgroundColor: 'var(--button-hover)'
              }
            }}
          >
            {!unlocked && <LockIcon sx={{ fontSize: 18, color: 'var(--button-text)' }} />}
            <Typography sx={{ fontSize: '0.9rem', color: 'var(--button-text)', fontWeight: 600 }}>
              {unlocked ? 'Jam' : 'Unlock'}
            </Typography>
          </Box>
        </Box>

        {/* Free badge : backing offert dans un pack payant */}
        {track.free && (
          <Box sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: '#10b981',
            px: 1,
            py: 0.25,
            borderRadius: '6px'
          }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Free
            </Typography>
          </Box>
        )}

        {/* Duration badge - bottom right like VideoCard */}
        <Box sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          backgroundColor: 'rgba(0,0,0,0.7)',
          px: 1,
          py: 0.25,
          borderRadius: '4px'
        }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'white' }}>
            {formatTime(currentTime)} / {duration ? formatTime(duration) : (track.duration || '0:00')}
          </Typography>
        </Box>
      </Box>

      {/* Info section - matches VideoCard */}
      <Box className="jam-backing-info" sx={{ p: 2, pt: 1 }}>
        {/* Badge row with artist info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {artist && (
            <Box
              onClick={() => setShowProfile(true)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1 }}
            >
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}99 100%)`,
                ...(artist.avatar && {
                  backgroundImage: `url(${artist.avatar})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {!artist.avatar && (
                  <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: 'white' }}>
                    {artist.name.split(' ').map(n => n[0]).join('')}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'white' }}>
                {artist.name}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: badgeColor,
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              flexShrink: 0
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white', textTransform: 'uppercase' }}>
              {badge}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', color: 'white', mb: 0.5 }}>
          {track.title}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
          {track.description}
        </Typography>

        {/* Difficulty ladder — jam this track at different scale levels */}
        {ladder.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {ladder.map(({ scale, difficulty }) => {
                const isCurrent = matchesCurrent(scale);
                const diffColor = DIFFICULTY_COLORS[difficulty];
                return (
                  <Box
                    key={scale}
                    onClick={() => handleJam(scale)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      backgroundColor: isCurrent ? `${accentColor}2e` : 'rgba(255,255,255,0.06)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${accentColor}26`
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {unlocked ? (
                        <PlayArrowIcon sx={{ fontSize: 18, color: accentColor }} />
                      ) : (
                        <LockIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.45)' }} />
                      )}
                      <Typography sx={{ fontSize: '0.85rem', color: 'white', fontWeight: isCurrent ? 600 : 400 }}>
                        {isMultiTonal ? scale : `${getDisplayRootNote(track.rootNote, scale)} ${scale}`}
                      </Typography>
                    </Box>
                    <Box sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      backgroundColor: `${diffColor}26`
                    }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: diffColor }}>
                        {SCALE_DIFFICULTY_LABELS[difficulty]}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {artist && (
        <ArtistProfileDialog
          open={showProfile}
          onClose={() => setShowProfile(false)}
          artist={artist}
        />
      )}
    </Box>
  );
};

export default BackingTrackCard;
